import type {
  IBuildFilterConditionsParams,
  IBuildVersionIdConditionParams,
} from '../filters/build-filter-conditions';
import type { IExtraCondition, GemXQlFilters, GemXQlVersionIds } from '../../types';

import {
  generateFilterConditions,
  buildVersionIdCondition,
  filterConfigs,
} from '../filters/build-filter-conditions';
import { buildExtraConditions } from '../filters/build-extra-conditions';
import { OPERATOR_IS, OPERATOR_IS_ONE_OF } from '../../constants';
import { CLAUSE_KEYWORD_BOUNDARY } from '../configs';

export interface IBuildConditionQueryParams
  extends IBuildFilterConditionsParams, IBuildVersionIdConditionParams {
  extraConditions?: IExtraCondition[];
}

export type IParseConditionQueryResult = {
  filters: GemXQlFilters;
  versionIds?: GemXQlVersionIds;
};

const build = ({
  filters,
  overrideFilters,
  versionIds,
  extraConditions,
}: IBuildConditionQueryParams) => {
  const conditions = generateFilterConditions(filters, overrideFilters);
  const versionIdConditions = buildVersionIdCondition(versionIds);
  const builtExtraConditions = buildExtraConditions(extraConditions);

  const allConditions = [...conditions, ...versionIdConditions, ...builtExtraConditions];

  return allConditions.length > 0 ? `WHERE ${allConditions.join(' AND ')}` : '';
};

const parse = (query: string): IParseConditionQueryResult => {
  const match = query.match(
    new RegExp(`\\bWHERE\\s+([\\s\\S]+?)(?=\\s+(?:${CLAUSE_KEYWORD_BOUNDARY})\\b|$)`, 'i'),
  );
  if (!match) return { filters: {} };

  const conditions = match[1].trim().split(/\s+AND\s+/i);
  const filters: Record<string, unknown> = {};
  const versionIds: Record<string, string> = {};

  conditions.forEach((condition) => {
    const trimmed = condition.trim();

    const inMatch = trimmed.match(/^(\S+)\s+IN\s+\((.+)\)$/i);
    if (inMatch) {
      const [, field, valuesStr] = inMatch;
      const values = valuesStr.split(',').map((v) => v.trim());
      const config = filterConfigs.find((c) => c.fieldName === field || c.multiField === field);
      if (config?.operator) {
        filters[config.multiField] = values;
        filters[config.operator] = OPERATOR_IS_ONE_OF;
      }
      return;
    }

    const eqMatch = trimmed.match(/^(\S+)\s+=\s+(.+)$/);
    if (eqMatch) {
      const [, field, value] = eqMatch;
      const config = filterConfigs.find((c) => c.fieldName === field || c.singleField === field);
      if (config) {
        filters[config.singleField] = value;
        if (config.operator) filters[config.operator] = OPERATOR_IS;
      } else {
        versionIds[field] = value;
      }
    }
  });

  return {
    filters: filters as GemXQlFilters,
    versionIds: Object.keys(versionIds).length > 0 ? versionIds : undefined,
  };
};

export { build as buildConditionQuery, parse as parseConditionQuery };
